import {KpModalDialogsPortal, type ModalDialogService} from 'shared/modal-dialogs';
import type {LocalizationService} from 'shared/providers';
import type {Subkind} from 'shared/constants';
import type {ErpFondPermissionsContextService} from 'src/features/erp';
import type {ReloaderService} from 'shared/services';
import {KpBrowserProgressBar} from 'shared/components/kp-browser-progress-bar';
import {KpToastMessages} from 'shared/components/kp-toast-messages';
import {KpFooter} from 'shared/components/kp-footer';
import {KpHeader} from 'shared/components/kp-header';
import type {Fond, User} from 'src/typings';
import type {IDocumentService, IHttpService, IOnInit} from 'angular';
import type {ModalWindowTypeToModelMap} from 'shared/modal-dialogs/config';
import type {TransitionService} from '@uirouter/angularjs';
import type {DefinedActionPerformer} from '../defined-actions/defined-action-performer';
import type ModalConversationInterceptorService from '../../modals/modal-conversation-interceptor.service';
import type RecordService from '../../features/record/record.service';
import type LoginService from '../login/login.service';
import type ActionRequestService from '../services/action-request.service';
import type FinishedResponseInteractionService from '../services/finished-response-interaction.service';
import type UserService from '../../features/user/user.service';
import {getUrlParams} from '../utils/url-utils';
import {isInterruptedActionResponse} from 'src/modals/modal-utils';
import {assertIsModalWindowType} from 'shared/modal-dialogs/utils';
import {AuthInterceptorRegistrar} from '../login/auth.interceptor-registrar';
import {UserInterceptorRegistrar} from 'src/features/user/user.interceptor-registrar';
import {SettingInterceptorRegistrar} from 'src/features/settings/setting.interceptor-registrar';
import {LoanInterceptorRegistrar} from 'src/features/loan/loan.interceptor-registrar';
import {PaymentInterceptorRegistrar} from 'src/features/payment/payment.interceptor-registrar';
import {DiscountInterceptorRegistrar} from 'src/features/discount-approving/lib/discount.interceptor-registrar';
import {RecordInterceptorRegistrar} from 'src/features/record/record.interceptor-registrar';
import KpMainMenu from 'shared/components/kp-main-menu/KpMainMenu.svelte';
import KpBarcodeScannerViewer from 'shared/barcode-scanner/kp-barcode-scanner-viewer/KpBarcodeScannerViewer.svelte';
import {ErpSidebarNav, ErpTopbar, ErpConnectionBar} from '../../features/erp/components';
import {ExemplarInterceptorRegistrar} from 'src/features/record/exemplar.interceptor-registrar';

export class PageController implements IOnInit {
    public static controllerName = 'PageCtrl';

    public autofocusGlobalSearch = false;
    public globalSearchInputEnabled: boolean;
    public fondPermissionsInitialized = false;

    public kpMainMenuComponent = KpMainMenu;
    public kpBarcodeScannerViewerComponent = KpBarcodeScannerViewer;
    public kpHeaderComponent = KpHeader;
    public kpFooterComponent = KpFooter;
    public kpToastMessagesComponent = KpToastMessages;
    public kpBrowserProgressBarComponent = KpBrowserProgressBar;
    public kpModalDialogsPortal = KpModalDialogsPortal;

    public erpSidebarNavComponent = ErpSidebarNav;
    public erpTopbarComponent = ErpTopbar;
    public erpConnectionBarComponent = ErpConnectionBar;

    /*@ngInject*/
    constructor(private loginService: LoginService,
                private actionRequestService: ActionRequestService,
                private modalDialogService: ModalDialogService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private $document: IDocumentService,
                private erpFondPermissionsContextService: ErpFondPermissionsContextService,
                localizationService: LocalizationService,
                definedActionPerformer: DefinedActionPerformer,
                modalConversationInterceptor: ModalConversationInterceptorService,
                recordService: RecordService,
                userService: UserService,
                reloader: ReloaderService,
                $http: IHttpService,
                $transitions: TransitionService,
                GLOBAL_SEARCH_INPUT_ENABLED: boolean
    ) {
        definedActionPerformer.registerMappedAction('record-holding-creation', (action) => recordService.createRecordHoldingAndAddExemplarsThrowingToModal(action.params?.subkind as Subkind, action.params?.fond as Fond));
        definedActionPerformer.registerMappedAction('record-holding-creation-no-record-presearch', (action) => recordService.createRecordHoldingAndAddExemplarsThrowingToModal(action.params?.subkind as Subkind, action.params?.fond as Fond, false));
        definedActionPerformer.registerMappedAction('extend-user-registration', async (action) => {
            await userService.extendRegistration({id: action.params?.id} as User);
            return reloader.reloadRoute();
        });

        new AuthInterceptorRegistrar(modalConversationInterceptor, loginService, $http).register();
        new UserInterceptorRegistrar(modalConversationInterceptor).register();
        new SettingInterceptorRegistrar(modalConversationInterceptor).register();
        new PaymentInterceptorRegistrar(modalConversationInterceptor).register();
        new DiscountInterceptorRegistrar(modalConversationInterceptor).register();
        new LoanInterceptorRegistrar(modalConversationInterceptor, localizationService).register();
        new RecordInterceptorRegistrar(modalConversationInterceptor).register();
        new ExemplarInterceptorRegistrar(modalConversationInterceptor).register();

        $transitions.onEnter({to: 'home'}, () => this.autofocusGlobalSearch = true);
        this.globalSearchInputEnabled = GLOBAL_SEARCH_INPUT_ENABLED;

        // Subscribe to fond permissions initialization state
        this.erpFondPermissionsContextService.fondPermissions$.subscribe((permissions) => {
            this.fondPermissionsInitialized = permissions.initialized;
        });

        this.openDialogsInChain();
    }

    public skipToContent() {
        this.$document[0].querySelector('main')?.focus();
    }

    public get shouldShowPageContent(): boolean {
        // Only apply fond permissions check when isSutorSutinLayout is true
        if (window.portaroConfiguration?.isSutorSutinLayout) {
            return this.fondPermissionsInitialized;
        }
        // For non-Sutor layouts, always show content
        return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    public async $onInit() {
        if (window.portaroConfiguration?.isSutorSutinLayout) {
            this.erpFondPermissionsContextService.setup();
        }

        await this.showLoginIfInParam();
        await this.doActionRequestIfInParam();
    }

    private async showLoginIfInParam() {
        const urlParams = getUrlParams();
        if (urlParams.showLogin) {
            const redirect = urlParams.redirect;
            if (redirect) {
                await this.loginService.goLogged(redirect);
            } else {
                await this.loginService.thisPageLogged();
            }
        }
    }

    private async doActionRequestIfInParam() {
        const urlParams = getUrlParams();
        if (urlParams.actionRequest) {
            const actionRequest = JSON.parse(urlParams.actionRequest);
            await this.actionRequestService.action(actionRequest.path, actionRequest.method, actionRequest.body);
        }
    }

    private async openDialogsInChain() {
        for (const dialog of window.dialogs) {
            try {
                assertIsModalWindowType(dialog.responseType);
                const actionResponse = await this.modalDialogService.openModalWindow(dialog.responseType, dialog as ModalWindowTypeToModelMap[typeof dialog.responseType]);
                await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            } catch (exceptionResponse) {
                if (!isInterruptedActionResponse(exceptionResponse)) {
                    this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
                }
            }
        }
    }
}
